/**
 * Main-thread client for `opencv-worker.ts`. The worker owns opencv; the
 * main thread only pushes pixels in and pulls a JSON-able result out.
 *
 * Vite's `?worker` suffix turns the import into a generated Worker constructor
 * that bundles the worker's dependency graph (opencv + analyzer) into its
 * own chunk. No custom URL or proxy plumbing.
 */

import OpencvWorker from './opencv-worker?worker';
import type { AnalysisResult } from 'trace-viewer/image-utils';
import type { AnalyzeRequest, AnalyzeResponse } from './opencv-worker';

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, { resolve: (r: AnalysisResult) => void; reject: (err: Error) => void }>();

function getWorker(): Worker {
  if (worker) return worker;
  worker = new OpencvWorker();
  worker.addEventListener('message', (event: MessageEvent<AnalyzeResponse>) => {
    const entry = pending.get(event.data.id);
    if (!entry) return;
    pending.delete(event.data.id);
    if (event.data.ok) entry.resolve(event.data.result);
    else entry.reject(new Error(event.data.error));
  });
  worker.addEventListener('error', (event) => {
    const msg = event.message || 'analysis worker error';
    for (const [, entry] of pending) entry.reject(new Error(msg));
    pending.clear();
  });
  return worker;
}

/**
 * Hand decoded pixels to the worker for analysis. Returns the raw
 * `AnalysisResult`; the orchestrator wraps it in an `ImageProbeReport`.
 *
 * `imageData.data.buffer` is transferred zero-copy so a 1600 × 1600 image
 * doesn't get cloned across the worker boundary.
 */
export function analyzeViaWorker(imageData: ImageData): Promise<AnalysisResult> {
  const w = getWorker();
  const id = nextId++;
  const request: AnalyzeRequest = { id, imageData };
  return new Promise<AnalysisResult>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage(request, [imageData.data.buffer]);
  });
}
